import { useState } from 'react'
import { ChevronDown, ChevronUp, Users, Target, MapPin, Award, UserCheck, Megaphone, Home, PoundSterling, GraduationCap, Clock, Settings, Brain, Shield, ClipboardCheck, FileCheck, Building, TrendingUp, CreditCard } from 'lucide-react'

// Sections data
export const sections = [
  {
    id: 'objective',
    title: 'Objective',
    icon: Target,
    content: {
      overview: 'Ensure the timely mobilisation of a fully trained, compliant and route-ready driver workforce to support Arriva in the West Yorkshire bus franchising rollout commencing in 2027.',
      details: [
        'Delivering drivers who meet all regulatory and operational requirements (PCV, CPC, Medical and Training)',
        'Retaining flexibility to respond to TUPE outcomes and phased depot transitions across West Yorkshire',
        'Providing a dependable, low-risk mobilisation solution that safeguards service continuity and protects Arriva\'s operational performance during a period of significant structural change'
      ],
      context: 'We understand that early franchise implementation is operationally sensitive. The first weeks of transition often involve shifting workforce numbers, compressed onboarding timelines and increased public scrutiny. Our role is to reduce that pressure by ensuring workforce readiness is controlled, visible and predictable.',
      summary: 'What differentiates C9 is not simply access to drivers, but a disciplined mobilisation process. Our integrated systems, structured onboarding stages and live reporting tools give Arriva\'s operational leadership clarity over driver readiness, compliance progression and deployment status — helping maintain stability throughout the transition.'
    }
  },
  {
    id: 'position',
    title: 'Strategic Position & Risks',
    icon: MapPin,
    content: {
      overview: 'The West Yorkshire franchising model represents a significant structural transition, with phased implementation commencing in 2027 and continuing through late 2028. This scale of change introduces operational, workforce and reputational risks that require structured mobilisation planning.',
      risks: [
        'Uncertainty around TUPE transfer volumes',
        'Driver attrition following transfer',
        'Competing operator retention incentives',
        'Phased depot transitions creating temporary resourcing gaps',
        'Public and political scrutiny associated with Combined Authority transport reform',
        'Risk of service disruption during early mobilisation phases'
      ],
      mitigation: 'C9\'s approach is designed to manage these risks practically and proactively. We focus on measured workforce scaling, controlled onboarding stages and clear operational reporting so that potential gaps are identified early rather than reactively addressed. Our objective is not simply to supply drivers, but to support Arriva in maintaining reliable service delivery and protecting its reputation during a high-profile public sector transition.'
    }
  },
  {
    id: 'mobilisation',
    title: 'Driver Mobilisation Overview',
    icon: Users,
    content: {
      requirements: [
        { label: 'Estimated Driver Requirement', value: 'To be confirmed in line with final PVR modelling, depot allocation and phased implementation schedule' },
        { label: 'Franchising Timeline', value: 'Franchising rollout commencing 2027 (phased implementation through late 2028)' },
        { label: 'Locations', value: 'West Yorkshire' },
        { label: 'Working Patterns', value: 'Full-time, part-time, weekend-only' },
        { label: 'Compliance Requirements', value: 'Valid PCV Licence, CPC, Medical & Training, Route learning, Route risk assessments, Vehicle type training, Company induction' }
      ],
      mobilisationModel: 'Our mobilisation model is underpinned by defined onboarding gateways, staged compliance verification and structured deployment planning. Each driver progresses through controlled assessment checkpoints, ensuring regulatory assurance and operational readiness before route allocation.'
    }
  },
  {
    id: 'expertise',
    title: 'C9 Expertise & Experience',
    icon: Award,
    content: {
      overview: 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, supported by access to a 35,000+ driver database and a proven track record of delivery across complex, multi-depot operations.',
      achievements: [
        'TUPE volatility and late-stage driver number shifts',
        'Short-notice contingency supply requirements',
        'Operational pressures during early franchise launch weeks',
        'Media and political scrutiny associated with Combined Authority transitions',
        'The importance of structured onboarding and route-readiness assurance'
      ],
      experience: 'Our Chief Executive, Alfie Rae, and Client Success Manager, Mustafa Ali, have direct, front-line experience working on the Greater Manchester Combined Authority (Bee Network) mobilisation during the transition to franchising. While operating under a different agency at that time, both were personally involved in the supply, onboarding and mobilisation of drivers during phased depot transfers.',
      beeNetworkMetrics: 'This experience provided invaluable insight into the challenges of franchising transitions.',
      innovation: 'The lessons learned during the GMCA transition directly inform how we operate today. That experience reinforced the importance of realistic scaling assumptions, early planning and close coordination between recruitment, compliance and operational teams. Our approach is built around risk mitigation, phased scaling capability, compliance-led onboarding and real-time operational oversight — ensuring a resilient and low-risk mobilisation solution for the West Yorkshire franchising rollout.'
    }
  },
  {
    id: 'management',
    title: 'Account Management & Support Structure',
    icon: UserCheck,
    content: {
      overview: 'We will deliver a robust and proactive account management and support structure to ensure a seamless partnership with Arriva for the West Yorkshire franchising rollout. Our approach is hands-on, collaborative, and performance-driven.',
      keyFeatures: [
        'Dedicated Mobilisation Team: A specialist team overseeing all aspects of mobilisation and delivery, ensuring that all requirements are met on time and to the highest standard.',
        'On-Site Management: Experienced personnel will be present at the depot to manage operations, support staff, and maintain consistent communication with all stakeholders.',
        'Real-Time Reporting: Live dashboards and performance reports will provide full visibility of progress and KPIs, enabling rapid response to any challenges.',
        '24/7 Operational Support: 24/7 operational support throughout the West Yorkshire mobilisation phase, including on-site presence during key deployment stages.',
        'Stakeholder Collaboration: We prioritise transparent communication and active engagement with all parties involved to ensure alignment and smooth delivery.',
        'Continuous Monitoring & Improvement: Ongoing performance evaluation and adaptive support strategies will maintain operational excellence throughout the project lifecycle.'
      ],
      leadership: {
        overview: 'Leadership & Local Commitment:',
        details: [
          'Alfie (Chief Executive) will lead on strategic delivery and oversight, ensuring alignment with franchise goals and overall project success.',
          'Mustafa Ali (Client Success Manager) will serve as the dedicated Account Manager, coordinating daily operations and acting as the primary day-to-day point of contact.',
          'Both Alfie and Mustafa will be based in West Yorkshire from the outset of assessments and throughout the mobilisation phase. They successfully led similar efforts in Manchester and the Bee Network, ensuring smooth launches and high standards of service.'
        ]
      },
      crossDepartmental: {
        overview: 'Cross-Departmental Support:',
        description: 'We will allocate a dedicated mobilisation team member from each of the following departments to ensure full-spectrum support:',
        departments: [
          {
            name: 'Compliance',
            icon: 'FileCheck',
            description: 'Ensuring all regulatory requirements, licensing, and safety standards are met throughout the mobilisation process.'
          },
          {
            name: 'Relocation & Housing',
            icon: 'Building',
            description: 'Managing accommodation arrangements and relocation support for drivers joining from outside the immediate region.'
          },
          {
            name: 'Marketing',
            icon: 'TrendingUp',
            description: 'Driver attraction campaigns, recruitment marketing, and brand representation throughout the mobilisation period.'
          },
          {
            name: 'Payroll',
            icon: 'CreditCard',
            description: 'Seamless payment processing, benefits administration, and financial support coordination for all deployed drivers.'
          }
        ]
      },
      sustainability: {
        title: 'Long-Term Sustainability:',
        description: 'Should resource support be required beyond the initial 12-week mobilisation period, we will appoint a local Account Manager based in West Yorkshire. This individual will be fully onboarded and trained by Alfie and Mustafa, replicating our successful model used during the Bee Network project.'
      },
      mobilisationLed: 'Unlike traditional agencies, C9 operates a mobilisation-led model rather than a reactive supply model. Dedicated account leadership, structured reporting and live dashboard visibility provide assurance to senior operational teams throughout the transition period.'
    }
  },
  {
    id: 'assessment',
    title: 'Assessment & Training Process',
    icon: ClipboardCheck,
    content: {
      overview: 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment.',
      stage1: {
        title: 'Stage 1: Name Check & Document Verification',
        details: 'ID, right-to-work checks, licence verification, and initial screening'
      },
      stage2: {
        title: 'Stage 2: Driving & Medical Assessment',
        details: 'On-road driving assessment by qualified assessors, drug & alcohol testing if required'
      },
      induction: {
        title: 'Induction',
        details: 'Induction within one week of successful assessment, introduction to depot procedures, ticketing systems, company policies'
      },
      route_learning: {
        title: 'Route Learning',
        details: 'Route learning plan provided; recommend starting with limited set of 3–5 routes for familiarity'
      },
      mentoring: {
        title: 'Mentoring',
        details: 'Drivers paired with experienced mentors or route guides for hands-on route training'
      },
      signoff: {
        title: 'Final Sign-off',
        details: 'Final route competency and area knowledge confirmed by designated mentor or supervisor'
      },
      structuredProcess: 'This structured process ensures every deployed driver is compliance-cleared, route-ready and performance-assured prior to entering live service, significantly reducing operational risk during franchise transitions.'
    }
  },
  {
    id: 'attraction',
    title: 'Driver Attraction Strategy',
    icon: Megaphone,
    content: {
      overview: 'Multi-channel driver attraction campaign with relocation strategy playing a pivotal role in attracting drivers outside of commutable distance.',
      audiences: {
        primary: [
          'Local PCV Drivers working for other bus and coach operators',
          'Drivers displaced from depot closures or operator reductions due to end of seasonal work'
        ],
        secondary: [
          'Local PCV holders who are currently inactive, semi-retired or have certain availability',
          'Part-time drivers overlooked by Bee Network operators who were unwilling to consider flexible working',
          'Relocation drivers from outside the immediate region'
        ]
      },
      channels: [
        'Digital Advertising: Indeed, CV-Library, social media (Facebook/Instagram), targeted PPC ads',
        'Job Fairs & Roadshows: Local events across West Yorkshire and nearby counties (South Yorkshire, Greater Manchester, North Yorkshire)',
        'Driver Referral Programme: Bonuses paid by C9 to existing drivers for successful referrals',
        'Industry Partnerships: Collaboration with PCV training schools, employment schemes, and logistics staffing groups'
      ]
    }
  },
  {
    id: 'relocation',
    title: 'Relocation Model',
    icon: Home,
    content: {
      overview: 'To widen the available talent pool and attract experienced drivers from outside the immediate region, we offer a structured relocation offering completely managed by C9 Recruitment.',
      model: 'Proven model highly utilised by the Bee Network due to local driver shortages. Accommodation paid for and managed by C9 Recruitment and invoiced back at cost.',
      accommodation: [
        'Fully serviced, high-quality accommodation sourced via professional property management company',
        'Properties typically range from 2 to 6 bedrooms, with each driver allocated a private bedroom where possible',
        'Communal areas include shared kitchen, bathroom(s), and living space, maintained to consistent standard',
        'Flexible 28-day rolling contracts to accommodate changing operational needs',
        'Costs fully inclusive of utilities (electricity, water, gas, internet, council tax)',
        'Security deposits may be required, subject to individual property terms',
        'Strategically located for ease of access to depots or route start points where possible'
      ],
      identification: 'Relocation drivers identified at initial vetting stage for clear understanding before onboarding completion.',
      safetyCompliance: 'All properties fully compliant with UK safety regulations, with up-to-date Gas Safety Certificates, Electrical Installation Condition Reports (EICR), and comprehensive Fire Safety Documentation maintained and readily available for inspection'
    }
  },
  {
    id: 'proposition',
    title: 'Competitive & Attractive Driver Package',
    icon: PoundSterling,
    content: {
      overview: 'Our driver proposition is designed to attract and retain high-quality drivers through competitive compensation and excellent working conditions.',
      packageStructure: {
        guaranteedHours: {
          title: 'Guaranteed Hours',
          details: '50 hours per week over 5 days'
        },
        shiftStructure: {
          title: 'Shift Structure', 
          details: '10-hour shifts with maximum 1-hour breaks'
        },
        overtime: {
          title: 'Overtime',
          details: 'Available on rest days for additional earnings'
        },
        marketPositioning: {
          title: 'Market Positioning',
          details: 'Benchmarked rates competitive with local market standards'
        },
        relocationSupport: {
          title: 'Relocation Allowance',
          details: 'Allows access to wider talent pool for rapid onboarding'
        }
      },
      disclaimer: 'Accurate 2027/2028 rates to be finalised following further discussion'
    }
  },
  {
    id: 'training',
    title: 'Assessment & Training (OPTIONAL Ridgeway * Beverley Bell Partnership)',
    icon: GraduationCap,
    content: {
      partnership: 'Ridgeway Training selected as preferred training provider. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader services.',
      leadership: 'Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector.',
      expertise: 'Expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers.',
      availability: 'This partnership is offered as an optional service enhancement, with both Ridgeway Training and Beverley Bell Consulting & Training available to provide comprehensive training solutions.',
      courses: [
        'OLAT (Operator Licence Awareness Training)',
        'TM CPC (Transport Manager Certificate of Professional Competence)',
        'First Aid training',
        'Mental Health awareness',
        'Driver CPC modules',
        'E-learning driver platform (in development)'
      ]
    }
  },
  {
    id: 'supply',
    title: 'Supply Model',
    icon: Clock,
    content: {
      commitment: 'Minimum 12-week commitment expected from temporary drivers, with potential for extension based on ongoing needs.',
      timeline: 'Assessments, training, and onboarding will take approximately 3 to 6 weeks, depending on specific training requirements.',
      mobilisation: 'Remaining period begins with mobilisation, drivers required consistently for first 1 to 4 weeks while TUPE drivers complete transfer and training.',
      standby: 'In the event that full cohort of drivers transfers across, our drivers remain on standby for remainder of 12-week period.',
      flexibility: 'Drivers continue to be paid guaranteed hours and remain available to cover sickness or holiday absences.'
    }
  },
  {
    id: 'onsite',
    title: 'Onsite Management',
    icon: Settings,
    content: {
      presence: 'Dedicated C9 operations presence at depot(s) to ensure seamless service delivery.',
      reporting: 'Live reporting and performance dashboard providing real-time insights.',
      monitoring: 'Continuous performance monitoring and support structure.',
      support: 'Real-time support structure ensuring immediate response to operational needs.'
    }
  },
  {
    id: 'technology',
    title: 'AI & Technology Integration',
    icon: Brain,
    content: {
      systems: [
        'Automated DVLA checks for drivers completed via direct DVLA API',
        'Comprehensive Applicant Tracking Dashboard providing real-time operational intelligence for operator oversight, featuring live driver deployment status, full utilisation metrics, accommodation occupancy tracking, shift patterns analysis, and complete check-in/check-out history with geolocation stamps',
        'Advanced candidate pipeline management system showing real-time progress through onboarding stages, assessment results, training completion status, and compliance verification milestones',
        'Driver ID Cards with scannable QR codes linking to comprehensive driver profiles including qualifications, route certifications, performance metrics, and current deployment status',
        'Route knowledge database accessible via QR system with interactive maps, traffic updates, depot information, and real-time operational alerts',
        'Seamless integration capabilities with existing DAS systems, payroll platforms, and third-party operational management tools'
      ],
      customisation: 'ATS configured in-house and can be tailored to meet specific data requirements.',
      future: 'Future development includes route integration and depot analytics dashboard capabilities.',
      integratedCapability: 'These system capabilities provide Arriva with practical visibility of the mobilisation pipeline. Licence validation, training progression, deployment status and accommodation allocation (where applicable) can be monitored in real time, supporting informed decision-making and operational control during early franchise implementation.'
    }
  },
  {
    id: 'compliance',
    title: 'Compliance Framework',
    icon: Shield,
    content: {
      principle: '"Powered by People, Driven by Compliance" - our guiding principle reflecting strong commitment to regulatory standards.',
      contribution: 'Frequent contributions to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff.',
      payment: 'All drivers are engaged via PAYE or FCSA-accredited Umbrella partnerships.',
      standards: 'Our compliance framework includes internal audit checkpoints, right-to-work verification, DVLA API licence validation and documented onboarding sign-off stages, providing clear and auditable assurance aligned with public sector governance expectations.'
    }
  },
  {
    id: 'committed-partner',
    title: 'Committed Partner',
    icon: Award,
    content: {
      overview: 'While our team has extensive experience mobilising and managing drivers for the Bee Network, we are not currently engaged with any other Bee Network operator for the supply of temporary drivers. This gives us access to a fresh, high-quality pool of candidates and, importantly, means we will not face any conflict of interest when allocating resources. Unlike agencies that support multiple operators in the North West, we will not be required to choose which network to prioritise. Our sole focus will be on supporting the West Yorkshire network. As the only large specialist in this position, we are uniquely positioned to provide our full commitment and deliver a successful mobilisation for Arriva.'
    }
  }
]

// Dashboard Card Component
export function DashboardCard({ section, theme, size, searchTerm, isExpanded: controlledExpanded, onToggleExpand }) {
  // Support both controlled and uncontrolled modes
  const [internalExpanded, setInternalExpanded] = useState(false)
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded
  const handleToggle = () => {
    if (onToggleExpand) {
      onToggleExpand()
    } else {
      setInternalExpanded(!internalExpanded)
    }
  }
  const Icon = section.icon

  const highlightText = (text) => {
    if (!searchTerm || !text) return text
    
    try {
      // Escape special regex characters but preserve the search term's meaning
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
      return text.replace(regex, '<mark class="bg-yellow-300 text-yellow-900 px-1 py-0.5 rounded font-medium">$1</mark>')
    } catch (error) {
      // If regex fails, return original text
      return text
    }
  }

  const getThemeClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:from-blue-100 hover:to-blue-150'
      case 'expertise':
        return 'bg-gradient-to-br from-c9-orange/10 to-c9-orange/20 border-orange-200 hover:from-c9-orange/20 hover:to-c9-orange/30'
      case 'operations':
        return 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:from-purple-100 hover:to-purple-150'
      case 'execution':
        return 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:from-green-100 hover:to-green-150'
      case 'support':
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 hover:from-gray-100 hover:to-gray-150'
      default:
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
    }
  }

  const getIconColor = () => {
    switch (theme) {
      case 'strategy': return 'text-blue-600'
      case 'expertise': return 'text-c9-orange'
      case 'operations': return 'text-purple-600'
      case 'execution': return 'text-green-600'
      case 'support': return 'text-gray-600'
      default: return 'text-c9-blue'
    }
  }

  const getSizeClasses = () => {
    return 'min-h-[300px]'
  }

  const getButtonClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
      case 'expertise':
        return 'bg-c9-orange text-white hover:bg-orange-600 hover:shadow-lg'
      case 'operations':
        return 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
      case 'execution':
        return 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
      case 'support':
        return 'bg-gray-600 text-white hover:bg-gray-700 hover:shadow-lg'
      default:
        return 'bg-c9-blue text-white hover:bg-blue-700 hover:shadow-lg'
    }
  }

  const getCardSummary = () => {
    switch (section.id) {
      case 'objective':
        return 'Ensure the timely mobilisation of a fully trained, compliant and route-ready driver workforce to support Arriva in the West Yorkshire bus franchising rollout commencing in 2027. This includes delivering drivers who meet all regulatory and operational requirements (PCV, CPC, Medical and Training), while retaining flexibility to respond to TUPE outcomes and phased depot transitions across West Yorkshire. Our objective is to provide a dependable, low-risk mobilisation solution that safeguards service continuity and protects Arriva\'s operational performance during a period of significant structural change. What differentiates C9 is not simply access to drivers, but a disciplined mobilisation process. Our integrated systems, structured onboarding stages and live reporting tools give Arriva\'s operational leadership clarity over driver readiness, compliance progression and deployment status.'
      case 'position':
        return 'The West Yorkshire franchising model represents a significant structural transition, with phased implementation commencing in 2027 and continuing through late 2028. This scale of change introduces operational, workforce and reputational risks that require structured mobilisation planning. Strategic risks identified include uncertainty around TUPE transfer volumes, driver attrition following transfer, competing operator retention incentives, phased depot transitions creating temporary resourcing gaps, public and political scrutiny associated with Combined Authority transport reform, and risk of service disruption during early mobilisation phases. C9\'s approach is designed to manage these risks practically and proactively. We focus on measured workforce scaling, controlled onboarding stages and clear operational reporting so that potential gaps are identified early rather than reactively addressed.'
      case 'expertise':
        return 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, supported by access to a 35,000+ driver database and a proven track record of delivery across complex, multi-depot operations. Our Chief Executive, Alfie Rae, and Client Success Manager, Mustafa Ali, have direct, front-line experience working on the Greater Manchester Combined Authority (Bee Network) mobilisation during the transition to franchising. This experience provided invaluable insight into TUPE volatility, short-notice contingency supply requirements, operational pressures during early franchise launch weeks, and the importance of structured onboarding and route-readiness assurance. The lessons learned during the GMCA transition directly inform how we operate today. That experience reinforced the importance of realistic scaling assumptions, early planning and close coordination between recruitment, compliance and operational teams.'
      case 'mobilisation':
        return 'Comprehensive strategic planning and implementation approach covering driver requirements aligned to final PVR modelling, depot allocation and phased award scope for the West Yorkshire franchising rollout commencing 2027 (phased implementation through late 2028). This includes managing full-time, part-time, and weekend-only working patterns while ensuring all compliance requirements are met: valid PCV licence, CPC certification, medical clearance, route learning, route risk assessments, vehicle type training, and company induction. Our mobilisation model is underpinned by defined onboarding gateways, staged compliance verification and structured deployment planning. Each driver progresses through controlled assessment checkpoints, ensuring regulatory assurance and operational readiness before route allocation.'
      case 'attraction':
        return 'Multi-channel driver attraction campaign designed to meet the scale and timeline of the West Yorkshire franchising mobilisation. Our strategy targets priority audiences including local PCV drivers working for other operators, drivers displaced from depot closures, local inactive PCV holders including semi-retired drivers, and national relocation candidates. We utilise digital advertising (Indeed, CV-Library, social media), job fairs and roadshows across West Yorkshire and nearby counties, comprehensive driver referral programmes with bonuses, and strategic industry partnerships with PCV training schools and employment schemes to ensure maximum reach and effectiveness.'
      case 'relocation':
        return 'Fully managed relocation offering designed to widen the available talent pool and attract experienced drivers from outside the immediate region. This proven model, highly utilised by the Bee Network due to local driver shortages, includes fully serviced, high-quality accommodation sourced via professional property management companies. Properties typically range from 2 to 6 bedrooms with private rooms for each driver where possible, communal facilities maintained to consistent standards, flexible 28-day rolling contracts, all-inclusive utilities (electricity, water, gas, internet, council tax), QR-coded tracking and vetting for each driver, and strategic location placement for optimal depot access.'
      case 'management':
        return 'We will deliver a robust and proactive account management and support structure to ensure a seamless partnership with Arriva for the West Yorkshire franchising rollout. Our hands-on, collaborative, and performance-driven approach includes a dedicated mobilisation team, on-site management presence, real-time reporting with live dashboards, 24/7 operational support throughout the West Yorkshire mobilisation phase including on-site presence during key deployment stages, transparent stakeholder collaboration, and continuous monitoring. Alfie (Chief Executive) will lead strategic delivery while Mustafa Ali (Client Success Manager) serves as the dedicated Account Manager, both based in West Yorkshire throughout the mobilisation phase, supported by cross-departmental team members from Compliance, Relocation & Housing, Marketing, and Payroll. Unlike traditional agencies, C9 operates a mobilisation-led model rather than a reactive supply model.'
      case 'assessment':
        return 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment. Stage 1 includes name check and document verification with ID, right-to-work checks, and licence verification. Stage 2 involves on-road driving assessment by qualified assessors with drug & alcohol testing if required. This is followed by induction within one week covering depot procedures, ticketing systems, and company policies, route learning plans starting with 3-5 routes for familiarity, mentoring with experienced route guides for hands-on training, and final route competency sign-off by designated supervisors. This structured process ensures every deployed driver is compliance-cleared, route-ready and performance-assured prior to entering live service, significantly reducing operational risk during franchise transitions.'
      case 'proposition':
        return 'Competitive and attractive driver proposition designed to attract and retain quality drivers throughout the mobilisation period. Key attractions include guaranteed 50 hours per week over 5 days, minimum 10-hour shifts with maximum 1-hour breaks, overtime opportunities available on rest days, and rates benchmarked competitively against market standards. This comprehensive package ensures we can attract experienced drivers while providing the financial incentives necessary for successful mobilisation.'
      case 'training':
        return 'Optional training partnership enhancement with Ridgeway Training and Beverley Bell Consulting & Training. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader specialised services. Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector. This optional partnership offers expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers, with comprehensive course offerings including OLAT, TM CPC, First Aid, Mental Health awareness, Driver CPC modules, and e-learning platforms in development.'
      case 'supply':
        return 'Structured supply model with 12-week minimum driver commitment, ensuring operational stability and continuity throughout the transition period. Our approach includes 3-6 week assessment, training, and onboarding phases, followed by active mobilisation support during the first 1-4 weeks while TUPE drivers complete their transfer and necessary training. In scenarios where full driver cohorts transfer across, our drivers remain on standby for the remainder of the 12-week period, continuing to receive guaranteed hours while remaining available to cover sickness, holiday absences, or any operational gaps. This provides operators with maximum flexibility and risk mitigation during the critical transition phase.'
      case 'technology':
        return 'Technology integration providing comprehensive workforce management and real-time operational intelligence. Our system features automated DVLA API integration, a comprehensive Applicant Tracking Dashboard with live operational metrics including driver deployment status, utilisation analytics, accommodation occupancy tracking, and complete check-in/check-out history with geolocation stamps. The platform includes candidate pipeline management, QR-coded driver ID cards linking to comprehensive profiles, an interactive route knowledge database, and seamless integration with existing DAS systems and payroll platforms. These system capabilities provide Arriva with practical visibility of the mobilisation pipeline. Licence validation, training progression, deployment status and accommodation allocation (where applicable) can be monitored in real time, supporting informed decision-making and operational control during early franchise implementation.'
      case 'compliance':
        return 'Our guiding principle "Powered by People, Driven by Compliance" reflects our unwavering commitment to regulatory standards and best practices. We frequently contribute to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff. All drivers are engaged via PAYE or FCSA-accredited Umbrella partnerships. Our compliance framework includes internal audit checkpoints, right-to-work verification, DVLA API licence validation and documented onboarding sign-off stages, providing clear and auditable assurance aligned with public sector governance expectations.'
      case 'committed-partner':
        return 'While our team has extensive experience mobilising and managing drivers for the Bee Network, we are not currently engaged with any other Bee Network operator for the supply of temporary drivers. This gives us access to a fresh, high-quality pool of candidates and, importantly, means we will not face any conflict of interest when allocating resources. Unlike agencies that support multiple operators in the North West, we will not be required to choose which network to prioritise. Our sole focus will be on supporting the West Yorkshire network. As the only large specialist in this position, we are uniquely positioned to provide our full commitment and deliver a successful mobilisation for Arriva.'
      default:
        return section.content.overview || section.content.details?.[0] || 'Comprehensive strategic planning and implementation approach covering all aspects of workforce mobilisation, operational readiness, and service delivery excellence.'
    }
  }

  return (
    <div className={`${getSizeClasses()} ${getThemeClasses()} rounded-lg border p-3 transition-all duration-300 hover:shadow-lg group h-full flex flex-col w-full`}>
      
      {/* Card Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
            <Icon className={`w-5 h-5 ${getIconColor()}`} />
          </div>
          <div>
            <h3 
              className="font-bold text-gray-800 text-base leading-tight group-hover:text-gray-900 transition-colors"
              dangerouslySetInnerHTML={{ __html: highlightText(section.title) }}
            />
            <div className="text-xs text-gray-600 mt-0.5">
              {theme === 'strategy' && 'Strategic Planning'}
              {theme === 'expertise' && 'Core Capabilities'}
              {theme === 'operations' && 'Operational Excellence'}
              {theme === 'execution' && 'Implementation'}
              {theme === 'support' && 'Technical Systems'}
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Preview Content */}
      <div className={`flex flex-col ${isExpanded ? '' : 'h-full'}`}>
        {/* Brief Summary */}
        <div className={`${isExpanded ? 'mb-4' : 'flex-grow mb-4'}`}>
          <div
            className="text-sm text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: highlightText(getCardSummary())
            }}
          />
        </div>

        {/* Expanded Content - appears right after summary when expanded */}
        {section.id !== 'committed-partner' && isExpanded && (
          <div className="mb-4 pt-4 border-t border-white/50 animate-fade-in">
            <SectionContent section={section} highlightText={highlightText} />
          </div>
        )}

        {/* Show Detail / Collapse Button - Always at bottom (hidden for committed-partner) */}
        {section.id !== 'committed-partner' && (
          <div className={isExpanded ? '' : 'mt-auto'}>
            {!isExpanded ? (
              <button
                className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                  getButtonClasses()
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggle()
                }}
              >
                <span>SHOW DETAILS</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <button
                className="w-full py-2 px-4 rounded-lg font-medium text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 flex items-center justify-center gap-2"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggle()
                }}
              >
                <ChevronUp className="w-4 h-4" />
                <span>COLLAPSE</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Section Content Component
export function SectionContent({ section, highlightText }) {
  const { content } = section

  switch (section.id) {
    case 'objective':
      return (
        <div className="space-y-3">
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">This includes:</h4>
            <ul className="space-y-1.5">
              {content.details.map((detail, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(detail) }}></span>
                </li>
              ))}
            </ul>
          </div>
          {content.context && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Why This Matters:</h4>
              <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.context) }}></p>
            </div>
          )}
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">What Differentiates C9:</h4>
            <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.summary) }}></p>
          </div>
        </div>
      )

    case 'position':
      return (
        <div className="space-y-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg border border-red-100">
            <h4 className="font-semibold text-red-800 mb-2 text-sm">Strategic Risks Identified:</h4>
            <ul className="space-y-1.5">
              {content.risks.map((risk, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-red-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(risk) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">C9 Mitigation Framework:</h4>
            <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.mitigation) }}></p>
          </div>
        </div>
      )

    case 'mobilisation':
      return (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            {content.requirements.map((req, idx) => (
              <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <dt className="font-semibold text-c9-blue text-sm" dangerouslySetInnerHTML={{ __html: highlightText(req.label) }}></dt>
                <dd className="text-neutral-700 mt-1 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(req.value) }}></dd>
              </div>
            ))}
          </div>
          {content.mobilisationModel && (
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 mt-4">
              <h4 className="font-semibold text-c9-blue mb-2">Our Mobilisation Model:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisationModel) }}></p>
            </div>
          )}
        </div>
      )

    case 'expertise':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Key Achievements:</h4>
              <ul className="space-y-2">
                {content.achievements.map((achievement, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(achievement) }}></span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Bee Network Experience:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.experience) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">Proven Scale & Performance:</h4>
                <p className="text-sm text-purple-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.beeNetworkMetrics) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Our Approach:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.innovation) }}></p>
              </div>
            </div>
          </div>
        </div>
      )

    case 'attraction':
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Primary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.primary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Secondary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.secondary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Attraction Channels:</h4>
              <ul className="space-y-2">
                {content.channels.map((channel, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(channel) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )

    case 'relocation':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Proven Model:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.model) }}></p>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Accommodation Features:</h4>
              <ul className="space-y-2">
                {content.accommodation.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(feature) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Driver Identification:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.identification) }}></p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2 text-sm">Safety & Compliance:</h4>
            <p className="text-sm text-red-700" dangerouslySetInnerHTML={{ __html: highlightText(content.safetyCompliance) }}></p>
          </div>
        </div>
      )

    case 'assessment':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.stage1.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage1.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.stage2.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage2.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-2 text-sm">{content.induction.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.induction.details) }}></p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">{content.route_learning.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.route_learning.details) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">{content.mentoring.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mentoring.details) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <h4 className="font-semibold text-green-800 mb-2 text-sm">{content.signoff.title}</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.signoff.details) }}></p>
              </div>
            </div>
          </div>
          {content.structuredProcess && (
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400 mt-4">
              <p className="text-sm text-gray-700 font-medium" dangerouslySetInnerHTML={{ __html: highlightText(content.structuredProcess) }}></p>
            </div>
          )}
        </div>
      )

    case 'proposition':
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(content.packageStructure).map(([key, item]) => (
              <div key={key} className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-c9-blue mb-2">{item.title}</h4>
                <p className="text-gray-700 text-sm" dangerouslySetInnerHTML={{ __html: highlightText(item.details) }}></p>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <p className="text-yellow-800 font-medium text-center" dangerouslySetInnerHTML={{ __html: highlightText(content.disclaimer) }}></p>
          </div>
        </div>
      )

    case 'training':
      return (
        <div className="space-y-4">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-6">
            <h4 className="font-semibold text-amber-800 mb-2 text-sm">Optional Service Enhancement</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.availability) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Partnership Structure:</h4>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.partnership) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-c9-blue mb-2 text-sm">Leadership Excellence:</h4>
                <p className="text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: highlightText(content.leadership) }}></p>
                <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.expertise) }}></p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Comprehensive Training Courses:</h4>
              <ul className="space-y-2">
                {content.courses.map((course, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(course) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Note:</h4>
            <p className="text-gray-700">The detailed Assessment & Training Process is covered in the Operations section above. This section focuses on the optional training partnership and course offerings available to enhance service delivery.</p>
          </div>
        </div>
      )

    case 'supply':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Commitment Period:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.commitment) }}></p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Timeline:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.timeline) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Mobilisation:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisation) }}></p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Flexibility:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.flexibility) }}></p>
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Standby Arrangements:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standby) }}></p>
          </div>
        </div>
      )

    case 'onsite':
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Operational Presence:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.presence) }}></p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Reporting:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.reporting) }}></p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Monitoring:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.monitoring) }}></p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Support Structure:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.support) }}></p>
          </div>
        </div>
      )

    case 'technology':
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Technology Systems:</h4>
            <ul className="space-y-2">
              {content.systems.map((system, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(system) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Customisation:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.customisation) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Future Development:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.future) }}></p>
            </div>
          </div>
          {content.integratedCapability && (
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 mt-4">
              <h4 className="font-semibold text-purple-800 mb-2 text-sm">What Sets Us Apart:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.integratedCapability) }}></p>
            </div>
          )}
        </div>
      )

    case 'compliance':
      return (
        <div className="space-y-4">
          <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-c9-orange">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Our Guiding Principle:</h4>
            <p className="text-lg font-medium text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.principle) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Industry Contribution:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.contribution) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Payment Structure:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.payment) }}></p>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Compliance Framework:</h4>
            <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standards) }}></p>
          </div>
        </div>
      )

    case 'management':
      return (
        <div className="space-y-4">
          {/* C9 Organisational Chart */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-lg border border-blue-200">
            <div className="bg-white p-6 rounded-lg shadow-lg border border-blue-200 max-w-4xl mx-auto">
  <h4 className="font-bold text-c9-blue text-2xl mb-6 text-center">C9 Arriva Project Leadership Structure</h4>
              
              {/* Leadership Level */}
              <div className="flex justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="relative">
                    <img 
                      src="/alf.png" 
                      alt="Alfie - Chief Executive" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-c9-blue shadow-lg mx-auto"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-c9-orange text-white text-xs px-2 py-1 rounded-full font-semibold">CEO</div>
                  </div>
                  <h5 className="font-bold text-c9-blue mt-3">Alfie Rae</h5>
                  <p className="text-sm text-gray-600">Chief Executive</p>
                  <p className="text-xs text-gray-500 mt-1">Strategic Delivery & Oversight</p>
                </div>
                
                <div className="text-center">
                  <div className="relative">
                    <img 
                      src="/mus.png" 
                      alt="Mustafa Ali - Client Success Manager" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-c9-blue shadow-lg mx-auto"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-c9-orange text-white text-xs px-2 py-1 rounded-full font-semibold">CSM</div>
                  </div>
                  <h5 className="font-bold text-c9-blue mt-3">Mustafa Ali</h5>
                  <p className="text-sm text-gray-600">Client Success Manager</p>
                  <p className="text-xs text-gray-500 mt-1">Dedicated Account Manager</p>
                </div>
              </div>
              
              {/* Connection Lines */}
              <div className="flex justify-center mb-6">
                <div className="w-64 h-px bg-gradient-to-r from-c9-blue via-c9-orange to-c9-blue"></div>
              </div>
              
              {/* Supporting Departments */}
              <div className="text-center mb-4">
                <h6 className="font-semibold text-gray-700 mb-4">Supported by Full C9 Organisation</h6>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <FileCheck className="w-6 h-6 text-c9-blue mx-auto mb-2" />
                    <p className="text-xs font-semibold text-c9-blue">Compliance Team</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <Building className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-green-600">Relocation & Housing</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-purple-600">Marketing Team</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <CreditCard className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-orange-600">Payroll & Finance</p>
                  </div>
                </div>
              </div>
              
              {/* Bottom note */}
              <div className="bg-c9-blue text-white p-3 rounded-lg text-center mt-6">
                <p className="text-sm font-medium">
                  📍 Both Alfie & Mustafa based in West Yorkshire throughout mobilisation phase
                </p>
              </div>
            </div>
          </div>

          {/* Key Leadership Summary */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Project Leadership (Supported by Full C9 Organisation)</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Alfie (Chief Executive)</strong> - Strategic delivery & oversight</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Mustafa Ali (Client Success Manager)</strong> - Dedicated Account Manager</span>
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs text-gray-600 italic">
                  Backed by our comprehensive team including recruitment specialists, compliance officers, operations coordinators, and administrative support staff across all departments.
                </p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Support Structure</h4>
              <p className="text-gray-700 text-sm">
                Dedicated mobilisation team members from Compliance, Relocation & Housing, Marketing, and Payroll departments ensuring full-spectrum support.
              </p>
            </div>
          </div>
          {content.mobilisationLed && (
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400 mt-4">
              <h4 className="font-semibold text-purple-800 mb-2">Mobilisation-Led Model:</h4>
              <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisationLed) }}></p>
            </div>
          )}
        </div>
      )

    default:
      return (
        <div className="text-gray-600">
          Content for {section.title} is being prepared...
        </div>
      )
  }
} 